import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import Modal from '../General/Modal';
import './CatalogPublishImpactModal.scss';

const DETAIL_LIMIT = 8;

const CatalogPublishImpactModal = ({ impact, isPublishing, onCancel, onConfirm }) => {
  const { summary, rates, lookbooks } = impact;
  const warnings = impact.warnings || [];
  const writes = impact.writes || {};
  const writeMetrics = [
    ['Variants minted', writes.variantsMinted],
    ['Siblings minted', writes.siblingsMinted],
    ['Rows refreshed', Number(writes.variantsUpdated || 0) + Number(writes.siblingsUpdated || 0)],
    ['Retired', writes.siblingsRetired]
  ];
  const metrics = [
    ['Lookbook changes', summary.lookbooksChanged],
    ['Rate conflicts', summary.rateConflicts]
  ];
  const hasDetails = summary.lookbooksChanged + summary.rateConflicts > 0;

  return (
    <Modal className='catalogs-publish-impact-modal' close={isPublishing ? () => {} : onCancel} title='Review publish impact'>
      <div className='catalogs-publish-impact-metrics writes'>
        {writeMetrics.map(([label, value]) => (
          <span key={label}><b>{Number(value || 0).toLocaleString()}</b>{label}</span>
        ))}
      </div>
      <div className='catalogs-publish-impact-metrics'>
        {metrics.map(([label, value]) => (
          <span key={label}><b>{Number(value || 0).toLocaleString()}</b>{label}</span>
        ))}
      </div>

      {warnings.map(warning => (
        <div className='catalogs-publish-impact-warning' key={warning.key}>
          <b>Override warning</b>
          <span>{warning.message}</span>
        </div>
      ))}

      {!hasDetails && <div className='catalogs-publish-impact-empty'>No lookbook changes or rate conflicts were found.</div>}

      <ImpactSection show={lookbooks.repointed.length + lookbooks.fallingBack.length > 0} title='Lookbook changes'>
        {limited(lookbooks.repointed).map(change => (
          <ImpactRow key={`lookbook-move-${change.item.id}`} label='Move' text={`${change.item.title}: ${change.source.name} → ${change.target.name}`} />
        ))}
        {limited(lookbooks.fallingBack).map(change => (
          <ImpactRow key={`lookbook-fallback-${change.item.id}`} label='Fallback' tone='warning' text={`${change.item.title} cannot be assigned automatically from ${change.source.name}.`} />
        ))}
        <MoreRows count={hiddenCount(lookbooks.repointed, lookbooks.fallingBack)} />
      </ImpactSection>

      <ImpactSection show={rates.conflicts.length > 0} title='Rate conflicts'>
        {limited(rates.conflicts).map((change, index) => (
          <ImpactRow
            key={`rate-conflict-${change.target.variantKey}-${index}`}
            label='Conflict'
            tone='warning'
            text={`${change.sources.map(source => `${source.source.name} ${rateLabel(source.rate)}`).join(', ')} → ${change.target.name}; ${rateLabel(change.selectedRate)} will remain.`}
          />
        ))}
        <MoreRows count={hiddenCount(rates.conflicts)} />
      </ImpactSection>

      <div className='catalogs-publish-impact-actions'>
        <button className='catalogs-button secondary' disabled={isPublishing} onClick={onCancel}>Cancel</button>
        <button className='catalogs-button' disabled={isPublishing} onClick={onConfirm}>
          {isPublishing ? <><FontAwesomeIcon className='catalogs-loading-icon' icon={faSpinner} spin />Publishing</> : warnings.length ? 'Override and publish' : 'Accept and publish'}
        </button>
      </div>
    </Modal>
  );
};

const ImpactSection = ({ children, show, title }) => {
  if (!show) return null;
  return <section className='catalogs-publish-impact-section'><h3>{title}</h3>{children}</section>;
};

const ImpactRow = ({ label, text, tone }) => (
  <div className={`catalogs-publish-impact-row${tone ? ` ${tone}` : ''}`}><b>{label}</b><span>{text}</span></div>
);

const MoreRows = ({ count }) => count ? <div className='catalogs-publish-impact-more'>And {count.toLocaleString()} more changes</div> : null;

const limited = rows => rows.slice(0, DETAIL_LIMIT);
const hiddenCount = (...groups) => groups.reduce((total, rows) => total + Math.max(0, rows.length - DETAIL_LIMIT), 0);
const rateLabel = rate => rate == null ? 'no selected rate' : `${Number(rate).toLocaleString()}%`;

export default CatalogPublishImpactModal;
